import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaFileContract,
  FaCheckCircle,
  FaExclamationTriangle,
  FaShieldAlt,
} from "react-icons/fa";
import { COUNTRY } from "../config/appConfig";

const TermsOfService = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const sections = [
    {
      title: "1. Acceptance of Terms",
      icon: FaCheckCircle,
      content: `By accessing and using Heads or Tails ${COUNTRY}, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you must not use our platform.`,
    },
    {
      title: "2. Eligibility",
      icon: FaShieldAlt,
      content: `You must be at least 18 years of age to use Heads or Tails ${COUNTRY}. By using our services, you represent and warrant that you are of legal age to form a binding contract and meet all eligibility requirements. We reserve the right to request proof of age at any time.`,
    },
    {
      title: "3. Account Registration",
      icon: FaFileContract,
      content: `To use certain features of our platform, you must register for an account. You agree to provide accurate, current, and complete information during registration. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.`,
    },
    {
      title: "4. Gaming Rules",
      icon: FaExclamationTriangle,
      content: `All games on Heads or Tails ${COUNTRY} operate using provably fair algorithms. The outcome of each game round is determined by our random number generator. By participating, you accept the game outcomes and agree not to dispute legitimate results.`,
    },
    {
      title: "5. Deposits and Withdrawals",
      icon: FaCheckCircle,
      content: `All deposits are processed via mobile money (MTN, Airtel, Zamtel). Minimum deposit and withdrawal amounts apply. Processing times may vary. We reserve the right to verify your identity before processing withdrawals. All transactions are final and non-refundable unless otherwise stated.`,
    },
    {
      title: "6. Prohibited Activities",
      icon: FaExclamationTriangle,
      content: `You agree not to: use our platform for any illegal purposes; attempt to manipulate game outcomes; use automated systems or bots; create multiple accounts; engage in fraudulent activities; or violate any applicable laws or regulations.`,
    },
    {
      title: "7. Intellectual Property",
      icon: FaShieldAlt,
      content: `All content on Heads or Tails ${COUNTRY}, including but not limited to text, graphics, logos, and software, is the property of Heads or Tails ${COUNTRY} and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.`,
    },
    {
      title: "8. Limitation of Liability",
      icon: FaFileContract,
      content: `Heads or Tails ${COUNTRY} is provided "as is" without warranties of any kind. We shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our platform. Our maximum liability shall not exceed the amount deposited by you in the last 30 days.`,
    },
    {
      title: "9. Modifications",
      icon: FaCheckCircle,
      content: `We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting. Your continued use of the platform after changes constitutes acceptance of the modified terms.`,
    },
    {
      title: "10. Contact Information",
      icon: FaShieldAlt,
      content: `For questions about these Terms of Service, please contact us at  call +260 76 057 9796.`,
      //support@Heads or Tails${COUNTRY}.com or
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#071129] to-[#0a1628] py-8 px-4">
      <motion.div
        className="max-w-4xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Back Button */}
        <motion.div variants={itemVariants} className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors duration-300"
          >
            <FaArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-emerald-500/30 mb-4">
            <FaFileContract className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Please read these terms carefully before using Heads or Tails{" "}
            {COUNTRY}. Last updated: January 2026
          </p>
        </motion.div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white mb-3">
                    {section.title}
                  </h2>
                  <p className="text-gray-400 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer Note */}
        <motion.div
          variants={itemVariants}
          className="mt-12 text-center text-gray-500 text-sm"
        >
          <p>
            By using Heads or Tails {COUNTRY}, you agree to these Terms of
            Service.
          </p>
          {/* <p className="mt-2">
            Questions? Contact us at{" "}
            <a
              href="mailto:support@Heads or Tails{COUNTRY}.com"
              className="text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              support@Heads or Tails{COUNTRY}.com
            </a>
          </p> */}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default TermsOfService;
